import * as React from 'react';

import { Alert, Box, Button, Stack } from '@mui/material';
import { Link } from 'react-router-dom';

import { AppSidebar } from '@app/components/sidebar/AppSidebar';
import { SidebarStateProvider } from '@app/components/sidebar/SidebarStateProvider';
import { Navigation } from '@app/components/topbar/Navigation';
import { AURA_TOUR_ANCHORS } from '@modules/_shared/components/chat/auraTour';
import {
  ChatPanelLayout,
  ChatPanelStateProvider,
  ChatToggleButton,
  useChatPanel,
} from '@modules/_shared/components/chat/chatPanel';
import { TopbarProvider } from '@modules/_shared/components/topbar/Topbar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { canAccessChat as computeCanAccessChat } from '@modules/_shared/utils/access';
import {
  useAutoTour,
  useTourReveal,
} from '@modules/product-updates/tours/TourProvider';
import { useLocale } from '@shared/hooks/locale';

type Props = {
  children: React.ReactNode;
};

const LayoutContent = (props: Props) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const canAccessChat = computeCanAccessChat(me);
  // "Show me" on the Aura entries, for users who have Aura.
  useAutoTour('aura-assistant', canAccessChat);
  const { open: chatOpen, toggle: toggleChat } = useChatPanel();
  // Its second step points at the message box: open the chat.
  useTourReveal(
    AURA_TOUR_ANCHORS.composer,
    () => {
      if (!chatOpen) {
        toggleChat();
      }
    },
    canAccessChat,
  );

  // ME_QUERY returns only the newest nylas-v3 login; a stale invalid row left
  // behind by a reconnect must not keep the banner up.
  const needNylasReconnect = me?.nylas_account?.[0]?.invalid === true;

  const innerContent = (
    <Stack
      css={{
        position: 'relative',
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
      }}
      sx={{
        flexShrink: 1,
        flexGrow: 1,
        flexDirection: 'column',
      }}
    >
      <Navigation extra={canAccessChat ? <ChatToggleButton /> : undefined} />
      {needNylasReconnect && (
        <Box css={{ margin: 8 }}>
          <Alert
            severity="error"
            action={
              <Button size="small" component={Link} to="/emails/preferences">
                {t('Go to settings')}
              </Button>
            }
          >
            {t(
              'Please reconnect your email account to resume email syncing, calendar events, and sending emails from your CRM.',
            )}
          </Alert>
        </Box>
      )}
      <Stack
        css={{
          position: 'relative',
          minHeight: 0,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        sx={{
          flexShrink: 1,
          flexGrow: 1,
          flexDirection: 'column',
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%',
            minHeight: '100%',
          }}
        >
          {props.children}
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <Box css={{ display: 'flex', width: '100%', height: '100dvh' }}>
      <AppSidebar />
      <Stack
        component="main"
        css={{
          position: 'relative',
          overflow: 'hidden',
          flexGrow: 1,
          minWidth: 0,
          ...(chatOpen && { height: '100dvh' }),
        }}
        sx={{
          flexDirection: 'column',
        }}
      >
        {canAccessChat ? (
          <ChatPanelLayout>{innerContent}</ChatPanelLayout>
        ) : (
          innerContent
        )}
      </Stack>
    </Box>
  );
};

export const Layout = (props: Props) => {
  return (
    <TopbarProvider>
      <SidebarStateProvider>
        <ChatPanelStateProvider>
          <LayoutContent {...props} />
        </ChatPanelStateProvider>
      </SidebarStateProvider>
    </TopbarProvider>
  );
};
